import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Loader2, MapPin, Save, Sparkles, User, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import GoogleAddressAutocomplete from './GoogleAddressAutocomplete';
import {
    COUNTRY_OPTIONS,
    DOCUMENT_TYPE_OPTIONS,
    GENDER_OPTIONS,
    deriveNationality,
    emptyExtendedProfile,
    parseExtendedProfile,
} from '../../utils/extendedProfile';
import { canAttemptFiscalCodeGeneration } from '../../utils/comuniLookup';
import { generateItalianFiscalCodeFromProfile } from '../../utils/italianFiscalCode';
import { parseGooglePlace } from '../../hooks/useGoogleMapsScript';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30';
const labelClass = 'mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300';
const readOnlyClass = 'w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:border-white/10 dark:bg-gray-900/60 dark:text-white/70';

function Field({ label, children, hint }) {
    return (
        <div>
            <label className={labelClass}>{label}</label>
            {children}
            {hint ? <p className="mt-1 text-xs text-gray-500 dark:text-white/50">{hint}</p> : null}
        </div>
    );
}

function SectionTitle({ icon: Icon, title }) {
    return (
        <h3 className="flex items-center gap-2 border-b border-gray-100 pb-2 text-base font-semibold text-gray-900 dark:border-white/10 dark:text-white">
            {Icon ? <Icon size={18} className="text-indigo-600 dark:text-indigo-400" /> : null}
            {title}
        </h3>
    );
}

function SectionPanel({ section, activeSection, children }) {
    if (activeSection && activeSection !== section) return null;
    return children;
}

export default function EditExtendedProfileModal({ client, isOpen, onClose, onSuccess, section = null }) {
    const { t } = useTranslation();
    const { getAuthenticatedApi } = useAuth();
    const api = getAuthenticatedApi();

    const [profile, setProfile] = useState(emptyExtendedProfile());
    const [membershipNotes, setMembershipNotes] = useState('');
    const [addressSearch, setAddressSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [generatingFiscalCode, setGeneratingFiscalCode] = useState(false);
    const [error, setError] = useState(null);

    const isSingleSection = Boolean(section);

    useEffect(() => {
        if (client && isOpen) {
            setProfile(parseExtendedProfile(client.extended_profile));
            setMembershipNotes(client.membership_notes || '');
            setAddressSearch('');
            setError(null);
        }
    }, [client, isOpen]);

    const setSectionField = useCallback((profileSection, field, value) => {
        setProfile((prev) => ({
            ...prev,
            [profileSection]: {
                ...prev[profileSection],
                [field]: value,
            },
        }));
    }, []);

    const handlePlaceSelected = useCallback((place) => {
        const parsed = parseGooglePlace(place);
        setProfile((prev) => ({
            ...prev,
            address: {
                ...prev.address,
                ...parsed,
            },
        }));
        setAddressSearch(place.formatted_address || parsed.street);
    }, []);

    const handleGenerateFiscalCode = async () => {
        if (!canAttemptFiscalCodeGeneration({
            fullName: client?.name,
            birthDate: profile.personal.birth_date,
            gender: profile.personal.gender,
            birthPlace: profile.personal.birth_place,
            birthPlaceZip: profile.personal.birth_place_zip,
        })) {
            toast.warn(t('gym.editExtendedProfile.messages.fiscalCodeIncomplete'));
            return;
        }
        try {
            setGeneratingFiscalCode(true);
            const code = await generateItalianFiscalCodeFromProfile({
                fullName: client?.name,
                birthDate: profile.personal.birth_date,
                gender: profile.personal.gender,
                birthPlace: profile.personal.birth_place,
                birthPlaceZip: profile.personal.birth_place_zip,
                api,
            });
            if (!code) {
                toast.warn(t('gym.editExtendedProfile.messages.fiscalCodeComuneNotFound'));
                return;
            }
            setSectionField('personal', 'fiscal_code', code);
            toast.success(t('gym.editExtendedProfile.messages.fiscalCodeGenerated'));
        } finally {
            setGeneratingFiscalCode(false);
        }
    };

    const nationality = deriveNationality(profile.personal.citizenship);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!client) return;

        try {
            setSaving(true);
            setError(null);

            let extendedProfileResponse = null;
            if (!section || section !== 'notes') {
                extendedProfileResponse = await api.put(`/users.php?id=${client.id}`, {
                    extended_profile: profile,
                });
            }

            if (!section || section === 'notes') {
                try {
                    await api.put(`/gym_members.php?user_id=${client.id}`, {
                        notes: membershipNotes,
                    });
                } catch (notesErr) {
                    // eslint-disable-next-line no-console
                    console.error('Errore salvataggio note interne:', notesErr);
                    toast.warn(t('gym.editExtendedProfile.messages.notesSaveError'));
                }
            }

            toast.success(
                section
                    ? t('gym.editExtendedProfile.messages.sectionUpdateSuccess')
                    : t('gym.editExtendedProfile.messages.updateSuccess'),
            );
            onSuccess?.({
                ...client,
                ...(extendedProfileResponse?.data?.user || {}),
                extended_profile: extendedProfileResponse?.data?.user?.extended_profile || profile,
                membership_notes: membershipNotes,
            });
            onClose();
        } catch (err) {
            const msg = err.response?.data?.error || t('gym.editExtendedProfile.messages.updateError');
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !client) return null;

    const fiscalReady = canAttemptFiscalCodeGeneration({
        fullName: client.name,
        birthDate: profile.personal.birth_date,
        gender: profile.personal.gender,
        birthPlace: profile.personal.birth_place,
        birthPlaceZip: profile.personal.birth_place_zip,
    });

    const modalTitle = section
        ? t(`gym.editExtendedProfile.sectionTitles.${section}`)
        : t('gym.editExtendedProfile.title');

    const saveLabel = section
        ? t('gym.editExtendedProfile.actions.saveSection')
        : t('gym.editExtendedProfile.actions.save');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className={`relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 ${isSingleSection ? 'max-w-xl' : 'max-w-3xl'}`}>
                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 pr-12 dark:border-white/10">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            {modalTitle}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-white/60">{client.name}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="absolute right-3 top-3 rounded-lg p-2 text-gray-500 hover:bg-black/5 disabled:opacity-50 dark:text-white/80 dark:hover:bg-white/10"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-5 py-6 sm:px-6">
                        {error && (
                            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
                                <AlertCircle size={18} />
                                {error}
                            </div>
                        )}

                        <SectionPanel section="contacts" activeSection={section}>
                            <section className="space-y-4">
                                <SectionTitle icon={User} title={t('gym.membersManager.memberDetail.profile.sections.contacts.title')} />
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.landline')}>
                                        <input type="tel" className={inputClass} value={profile.contacts.landline} onChange={(e) => setSectionField('contacts', 'landline', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.secondaryEmail')}>
                                        <input type="email" className={inputClass} value={profile.contacts.secondary_email} onChange={(e) => setSectionField('contacts', 'secondary_email', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.emergencyContact')}>
                                        <input type="text" className={inputClass} value={profile.contacts.emergency_contact} onChange={(e) => setSectionField('contacts', 'emergency_contact', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.emergencyPhone')}>
                                        <input type="tel" className={inputClass} value={profile.contacts.emergency_phone} onChange={(e) => setSectionField('contacts', 'emergency_phone', e.target.value)} disabled={saving} />
                                    </Field>
                                </div>
                            </section>
                        </SectionPanel>

                        <SectionPanel section="personal" activeSection={section}>
                            <section className="space-y-4">
                                <SectionTitle icon={User} title={t('gym.membersManager.memberDetail.profile.sections.personal.title')} />
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.birthDate')}>
                                        <input type="date" className={inputClass} value={profile.personal.birth_date} onChange={(e) => setSectionField('personal', 'birth_date', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.gender')}>
                                        <select className={inputClass} value={profile.personal.gender} onChange={(e) => setSectionField('personal', 'gender', e.target.value)} disabled={saving}>
                                            <option value="">{t('gym.editExtendedProfile.placeholders.select')}</option>
                                            {GENDER_OPTIONS.map((g) => (
                                                <option key={g.value} value={g.value}>{g.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.birthPlace')}>
                                        <input type="text" className={inputClass} value={profile.personal.birth_place} onChange={(e) => setSectionField('personal', 'birth_place', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.birthPlaceZip')} hint={t('gym.editExtendedProfile.hints.birthPlaceZip')}>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className={inputClass}
                                            maxLength={5}
                                            value={profile.personal.birth_place_zip}
                                            onChange={(e) => setSectionField('personal', 'birth_place_zip', e.target.value.replace(/\D/g, '').slice(0, 5))}
                                            disabled={saving}
                                        />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.citizenship')}>
                                        <select className={inputClass} value={profile.personal.citizenship} onChange={(e) => setSectionField('personal', 'citizenship', e.target.value)} disabled={saving}>
                                            {COUNTRY_OPTIONS.map((c) => (
                                                <option key={c.code} value={c.code}>{c.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.nationality')} hint={t('gym.editExtendedProfile.hints.nationalityAuto')}>
                                        <input type="text" className={readOnlyClass} value={nationality} readOnly tabIndex={-1} />
                                    </Field>
                                    <div className="sm:col-span-2">
                                        <Field label={t('gym.membersManager.memberDetail.profile.fields.fiscalCode')}>
                                            <div className="flex flex-col gap-2 sm:flex-row">
                                                <input type="text" className={inputClass} value={profile.personal.fiscal_code} onChange={(e) => setSectionField('personal', 'fiscal_code', e.target.value.toUpperCase())} disabled={saving} maxLength={16} />
                                                <button
                                                    type="button"
                                                    onClick={handleGenerateFiscalCode}
                                                    disabled={saving || generatingFiscalCode || !fiscalReady}
                                                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    <Sparkles size={16} />
                                                    {t('gym.editExtendedProfile.actions.generateFiscalCode')}
                                                </button>
                                            </div>
                                        </Field>
                                    </div>
                                </div>
                            </section>
                        </SectionPanel>

                        <SectionPanel section="address" activeSection={section}>
                            <section className="space-y-4">
                                <SectionTitle icon={MapPin} title={t('gym.membersManager.memberDetail.profile.sections.address.title')} />
                                <Field label={t('gym.editExtendedProfile.fields.addressSearch')} hint={t('gym.editExtendedProfile.hints.addressGoogle')}>
                                    <GoogleAddressAutocomplete
                                        value={addressSearch}
                                        onChange={setAddressSearch}
                                        onPlaceSelected={handlePlaceSelected}
                                        disabled={saving}
                                    />
                                </Field>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.address')}>
                                        <input type="text" className={inputClass} value={profile.address.street} onChange={(e) => setSectionField('address', 'street', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.city')}>
                                        <input type="text" className={inputClass} value={profile.address.city} onChange={(e) => setSectionField('address', 'city', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.province')}>
                                        <input type="text" className={inputClass} maxLength={2} value={profile.address.province} onChange={(e) => setSectionField('address', 'province', e.target.value.toUpperCase())} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.zip')}>
                                        <input type="text" className={inputClass} value={profile.address.zip} onChange={(e) => setSectionField('address', 'zip', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.country')}>
                                        <select className={inputClass} value={profile.address.country} onChange={(e) => setSectionField('address', 'country', e.target.value)} disabled={saving}>
                                            {COUNTRY_OPTIONS.map((c) => (
                                                <option key={c.code} value={c.code}>{c.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>
                            </section>
                        </SectionPanel>

                        <SectionPanel section="identity" activeSection={section}>
                            <section className="space-y-4">
                                <SectionTitle title={t('gym.membersManager.memberDetail.profile.sections.identity.title')} />
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.documentType')}>
                                        <select className={inputClass} value={profile.identity.document_type} onChange={(e) => setSectionField('identity', 'document_type', e.target.value)} disabled={saving}>
                                            <option value="">{t('gym.editExtendedProfile.placeholders.select')}</option>
                                            {DOCUMENT_TYPE_OPTIONS.map((d) => (
                                                <option key={d.value} value={d.value}>{d.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.documentNumber')}>
                                        <input type="text" className={inputClass} value={profile.identity.document_number} onChange={(e) => setSectionField('identity', 'document_number', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.documentIssueDate')}>
                                        <input type="date" className={inputClass} value={profile.identity.document_issue_date} onChange={(e) => setSectionField('identity', 'document_issue_date', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.documentExpiry')}>
                                        <input type="date" className={inputClass} value={profile.identity.document_expiry} onChange={(e) => setSectionField('identity', 'document_expiry', e.target.value)} disabled={saving} />
                                    </Field>
                                </div>
                            </section>
                        </SectionPanel>

                        <SectionPanel section="health" activeSection={section}>
                            <section className="space-y-4">
                                <SectionTitle title={t('gym.membersManager.memberDetail.profile.sections.health.title')} />
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.medicalCertificate')}>
                                        <input type="text" className={inputClass} value={profile.health.medical_certificate} onChange={(e) => setSectionField('health', 'medical_certificate', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.medicalCertExpiry')}>
                                        <input type="date" className={inputClass} value={profile.health.medical_cert_expiry} onChange={(e) => setSectionField('health', 'medical_cert_expiry', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.insuranceNumber')}>
                                        <input type="text" className={inputClass} value={profile.health.insurance_number} onChange={(e) => setSectionField('health', 'insurance_number', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.insuranceExpiry')}>
                                        <input type="date" className={inputClass} value={profile.health.insurance_expiry} onChange={(e) => setSectionField('health', 'insurance_expiry', e.target.value)} disabled={saving} />
                                    </Field>
                                    <div className="sm:col-span-2">
                                        <Field label={t('gym.membersManager.memberDetail.profile.fields.attachments')}>
                                            <textarea className={inputClass} rows={2} value={profile.health.attachments} onChange={(e) => setSectionField('health', 'attachments', e.target.value)} disabled={saving} />
                                        </Field>
                                    </div>
                                </div>
                            </section>
                        </SectionPanel>

                        <SectionPanel section="professional" activeSection={section}>
                            <section className="space-y-4">
                                <SectionTitle title={t('gym.membersManager.memberDetail.profile.sections.professional.title')} />
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.profession')}>
                                        <input type="text" className={inputClass} value={profile.professional.profession} onChange={(e) => setSectionField('professional', 'profession', e.target.value)} disabled={saving} />
                                    </Field>
                                    <Field label={t('gym.membersManager.memberDetail.profile.fields.company')}>
                                        <input type="text" className={inputClass} value={profile.professional.company} onChange={(e) => setSectionField('professional', 'company', e.target.value)} disabled={saving} />
                                    </Field>
                                </div>
                            </section>
                        </SectionPanel>

                        <SectionPanel section="notes" activeSection={section}>
                            <section className="space-y-4">
                                <SectionTitle title={t('gym.membersManager.memberDetail.profile.sections.notes.title')} />
                                <Field label={t('gym.membersManager.memberDetail.profile.fields.internalNotes')} hint={t('gym.editExtendedProfile.hints.internalNotes')}>
                                    <textarea className={inputClass} rows={3} value={membershipNotes} onChange={(e) => setMembershipNotes(e.target.value)} disabled={saving} />
                                </Field>
                            </section>
                        </SectionPanel>
                    </div>

                    <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 px-5 py-4 dark:border-white/10">
                        <button type="button" onClick={onClose} disabled={saving} className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-50 dark:bg-gray-800 dark:text-white">
                            {t('gym.editExtendedProfile.actions.cancel')}
                        </button>
                        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {saving ? t('gym.editExtendedProfile.actions.saving') : saveLabel}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
